/**
 * Icon Component
 */

import React, { useRef } from 'react';
import icons from 'mlb-icons/json/iconsbyIds.json';
import { IconProps, IconSizes } from './icon.types';
import { IconWrapper } from './icon.style';
import { IconWrapperOverrides, IconColorOverrides } from './utils/styleOverrides';
import COLORS from '../../style/universal.color';

import buildSvg, { IconDataProps } from './utils/buildSvg';
import generateId from '../../utils/generateId';

const Icon = ({
  allowColorOverride = true,
  allowStyleOverride = true,
  className,
  description,
  fallback,
  fill,
  name,
  orientation = 0,
  role,
  size = IconSizes.AUTO,
  width,
  ...props
}: IconProps) => {
  const icon = icons[name as keyof typeof icons] || (fallback && icons[fallback as keyof typeof icons]);

  // If the icon wasn't found, don't return any
  if (!icon) {
    return null;
  }

  // Keep track of the size value for the svg building
  // as well as a string for the styling
  let sizeNum: number;
  let sizeStyle: string;

  if (width) {
    sizeNum = width;
    sizeStyle = `${width}px`;
  } else {
    switch (size) {
      case IconSizes.SM:
        sizeNum = 12;
        sizeStyle = `${sizeNum}px`;
        break;
      case IconSizes.MD:
        sizeNum = 16;
        sizeStyle = `${sizeNum}px`;
        break;
      case IconSizes.LG:
        sizeNum = 24;
        sizeStyle = `${sizeNum}px`;
        break;
      default:
        sizeNum = 24;
        sizeStyle = `100%`;
        break;
    }
  }

  const content = buildSvg(icon.children as IconDataProps[]);

  // If we're overriding styles, use the specific wrapper for the icon (if it exists)
  const Wrapper =
    (allowStyleOverride && IconWrapperOverrides[name as keyof typeof IconWrapperOverrides]) || IconWrapper;
  // If we're overriding colors, use the default color for the icon only if fill isn't passed.
  const useFill: string =
    fill || (allowColorOverride && IconColorOverrides[name as keyof typeof IconColorOverrides]) || COLORS.mlbGrey5;

  const { current: iconId } = useRef(generateId(`${icon.id}-`));

  return (
    <Wrapper
      className={className}
      $sizeStyle={sizeStyle}
      fill={useFill}
      transform={`rotate(${orientation})`}
      viewBox={`0 0 ${sizeNum} ${sizeNum}`}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <use xlinkHref={`#${iconId}`}>
        <symbol
          xmlns={icon.attributes.xmlns}
          viewBox={(icon.attributes as { viewBox: string }).viewBox}
          id={`${iconId}`}
          key={`${iconId}`}
          role={role}
        >
          <title id={`title-${iconId}`}>{iconId}</title>
          {description && <desc id={`desc-${iconId}`}>{description}</desc>}
          {content}
        </symbol>
      </use>
    </Wrapper>
  );
};

export default Icon;
